import "../components/breed-combobox/breed-combobox.css";
import { getBreedsForSpecies } from "./breeds";

function normalize(value: string): string {
  return value.trim().toLowerCase();
}

function filterBreeds(breeds: readonly string[], query: string): string[] {
  const q = normalize(query);
  if (!q) return [...breeds];
  return breeds.filter((b) => normalize(b).includes(q));
}

function ensureComboboxShell(breedInput: HTMLInputElement) {
  const existing = breedInput.closest(".breed-combobox") as HTMLElement | null;
  if (existing) {
    return {
      root: existing,
      input: breedInput,
      toggle: existing.querySelector(".breed-combobox-toggle") as HTMLButtonElement,
      dropdown: existing.querySelector(".breed-combobox-dropdown") as HTMLDivElement,
      search: existing.querySelector(".breed-combobox-search") as HTMLInputElement,
      list: existing.querySelector(".breed-combobox-options") as HTMLUListElement,
    };
  }

  const root = document.createElement("div");
  root.className = "breed-combobox";

  const parent = breedInput.parentNode;
  if (!parent) throw new Error("Breed input has no parent");
  parent.insertBefore(root, breedInput);
  root.appendChild(breedInput);

  breedInput.classList.add("breed-combobox-input");
  breedInput.removeAttribute("list");
  breedInput.setAttribute("autocomplete", "off");
  breedInput.setAttribute("role", "combobox");
  breedInput.setAttribute("aria-autocomplete", "list");
  breedInput.setAttribute("aria-expanded", "false");

  const toggle = document.createElement("button");
  toggle.type = "button";
  toggle.className = "breed-combobox-toggle";
  toggle.setAttribute("aria-label", "Apri elenco razze");
  toggle.innerHTML =
    '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" aria-hidden="true"><path d="m6 9 6 6 6-6"/></svg>';

  const dropdown = document.createElement("div");
  dropdown.className = "breed-combobox-dropdown";

  const search = document.createElement("input");
  search.type = "search";
  search.className = "breed-combobox-search";
  search.placeholder = "Cerca razza…";
  search.setAttribute("autocomplete", "off");
  search.setAttribute("aria-label", "Cerca razza");

  const list = document.createElement("ul");
  list.className = "breed-combobox-options";
  list.setAttribute("role", "listbox");

  dropdown.append(search, list);
  root.append(toggle, dropdown);

  return { root, input: breedInput, toggle, dropdown, search, list };
}

export function bindBreedCombobox(
  speciesSelect: HTMLSelectElement,
  breedInput: HTMLInputElement,
): () => void {
  const shell = ensureComboboxShell(breedInput);
  let breeds: string[] = [];
  let activeIndex = -1;
  let enabled = false;

  const setOpen = (open: boolean) => {
    shell.root.classList.toggle("is-open", open);
    shell.input.setAttribute("aria-expanded", open ? "true" : "false");
    if (!open) {
      shell.search.value = "";
      activeIndex = -1;
    }
  };

  const renderOptions = (query = "") => {
    const filtered = filterBreeds(breeds, query);
    shell.list.innerHTML = "";

    if (filtered.length === 0) {
      const empty = document.createElement("li");
      empty.className = "breed-combobox-empty";
      empty.textContent = "Nessuna razza trovata";
      shell.list.appendChild(empty);
      activeIndex = -1;
      return;
    }

    filtered.forEach((breed, index) => {
      const item = document.createElement("li");
      item.className = "breed-combobox-option";
      item.textContent = breed;
      item.setAttribute("role", "option");
      item.dataset.index = String(index);
      if (index === activeIndex) item.classList.add("is-active");
      item.addEventListener("mousedown", (e) => {
        e.preventDefault();
        shell.input.value = breed;
        setOpen(false);
      });
      shell.list.appendChild(item);
    });
  };

  const openDropdown = (focusSearch = false) => {
    if (!enabled) return;
    setOpen(true);
    renderOptions(shell.search.value || shell.input.value);
    if (focusSearch) shell.search.focus();
  };

  const refresh = () => {
    breeds = getBreedsForSpecies(speciesSelect.value);
    enabled = breeds.length > 0;
    shell.root.classList.toggle("breed-combobox--plain", !enabled);
    shell.toggle.hidden = !enabled;
    shell.dropdown.hidden = !enabled;

    if (!enabled) {
      setOpen(false);
      shell.input.placeholder = "Razza (testo libero)";
      return;
    }

    shell.input.placeholder = "Razza (cerca o digita)";
    renderOptions(shell.search.value || shell.input.value);
  };

  const onDocumentPointerDown = (e: Event) => {
    if (!shell.root.contains(e.target as Node)) setOpen(false);
  };

  const onSpeciesChange = () => refresh();
  const onInputFocus = () => openDropdown(false);
  const onInputInput = () => {
    if (!enabled) return;
    openDropdown(false);
    activeIndex = -1;
    renderOptions(shell.input.value);
  };
  const onToggleClick = () => {
    if (shell.root.classList.contains("is-open")) setOpen(false);
    else openDropdown(true);
  };
  const onSearchInput = () => {
    activeIndex = -1;
    renderOptions(shell.search.value);
  };
  const onSearchKeyDown = (e: KeyboardEvent) => {
    const options = shell.list.querySelectorAll<HTMLElement>(".breed-combobox-option");
    if (e.key === "ArrowDown") {
      e.preventDefault();
      activeIndex = Math.min(activeIndex + 1, options.length - 1);
      renderOptions(shell.search.value);
      options[activeIndex]?.scrollIntoView({ block: "nearest" });
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      activeIndex = Math.max(activeIndex - 1, 0);
      renderOptions(shell.search.value);
      options[activeIndex]?.scrollIntoView({ block: "nearest" });
    } else if (e.key === "Enter") {
      e.preventDefault();
      const selected = options[activeIndex];
      if (selected) {
        shell.input.value = selected.textContent ?? "";
        setOpen(false);
        shell.input.focus();
      }
    } else if (e.key === "Escape") {
      e.preventDefault();
      setOpen(false);
      shell.input.focus();
    }
  };
  const onInputKeyDown = (e: KeyboardEvent) => {
    if (e.key === "ArrowDown" && enabled) {
      e.preventDefault();
      openDropdown(true);
    }
  };

  speciesSelect.addEventListener("change", onSpeciesChange);
  shell.input.addEventListener("focus", onInputFocus);
  shell.input.addEventListener("input", onInputInput);
  shell.input.addEventListener("keydown", onInputKeyDown);
  shell.toggle.addEventListener("click", onToggleClick);
  shell.search.addEventListener("input", onSearchInput);
  shell.search.addEventListener("keydown", onSearchKeyDown);
  document.addEventListener("pointerdown", onDocumentPointerDown);

  refresh();

  return () => {
    speciesSelect.removeEventListener("change", onSpeciesChange);
    shell.input.removeEventListener("focus", onInputFocus);
    shell.input.removeEventListener("input", onInputInput);
    shell.input.removeEventListener("keydown", onInputKeyDown);
    shell.toggle.removeEventListener("click", onToggleClick);
    shell.search.removeEventListener("input", onSearchInput);
    shell.search.removeEventListener("keydown", onSearchKeyDown);
    document.removeEventListener("pointerdown", onDocumentPointerDown);
    setOpen(false);
  };
}
